import { useMutation } from "@tanstack/react-query";
import {
  CheckIcon,
  CreditCardIcon,
  LandmarkIcon,
  Trash2Icon,
} from "lucide-react";
import { useId, useState } from "react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { ConfirmationModal } from "@/components/confirmation-modal";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { SegmentedTabs } from "@/components/segmented-tabs";

import type {
  PaymentMethod,
  PaymentMethods,
  PaymentMethodSlot,
  PaymentMethodType,
} from "../types";

import { usePaymentMethodMutation } from "../hooks/usePaymentMethods";
import { paymentMethodSummary } from "../utils/payment-method-format";
import { getMethodInSlot } from "../utils/payment-method-slots";
import { AchPaymentForm } from "./AchPaymentForm";
import { CardPaymentForm } from "./CardPaymentForm";

interface PaymentMethodModalProps {
  readonly onClose: () => void;
  readonly paymentMethods: PaymentMethods;
  readonly projectId: number;
  readonly slot: null | PaymentMethodSlot;
}

export function PaymentMethodModal({
  onClose,
  paymentMethods,
  projectId,
  slot,
}: PaymentMethodModalProps) {
  const formId = useId();
  const currentMethod = slot ? getMethodInSlot(paymentMethods, slot) : null;
  const [methodType, setMethodType] = useState<PaymentMethodType>(
    currentMethod?.type ?? "card",
  );
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const savePaymentMethod = usePaymentMethodMutation(projectId);
  const removePaymentMethod = usePaymentMethodMutation(projectId);

  const submit = useMutation({
    mutationFn: async (resolveMethod: () => Promise<PaymentMethod>) => {
      const method = await resolveMethod();

      await savePaymentMethod.mutateAsync(
        slot
          ? { method, setAsDefault, slot, type: "update" }
          : { method, setAsDefault, type: "add" },
      );
    },
    onSuccess: onClose,
  });

  const handleTypeChange = (type: PaymentMethodType) => {
    setMethodType(type);
    setIsFormValid(false);
    submit.reset();
  };

  const handleRemove = () => {
    if (slot?.kind !== "backup") return;

    removePaymentMethod.mutate(
      { index: slot.index, type: "remove" },
      {
        onSuccess: () => {
          setIsRemoveOpen(false);
          onClose();
        },
      },
    );
  };

  const openRemoveConfirmation = () => {
    removePaymentMethod.reset();
    setIsRemoveOpen(true);
  };

  const updateLabel = methodType === "card" ? "Update card" : "Update account";
  const submitLabel = slot ? updateLabel : "Add backup method";

  const willReflowBackup =
    slot?.kind === "backup" &&
    slot.index === 0 &&
    paymentMethods.backupMethods.length > 1;

  return (
    <>
      <Modal
        classNames={{
          modal: "flex h-full w-full flex-col sm:h-auto sm:w-144",
        }}
        isDismissable
        isOpen
        onOpenChange={(isOpen) => !isOpen && onClose()}
      >
        <Dialog className="h-full">
          <DialogCloseButton />
          <DialogHeader className="px-xl pt-2xl text-lg font-semibold sm:border-b sm:border-border-secondary sm:px-3xl sm:pb-2xl sm:pt-3xl">
            {slot ? "Update payment method" : "Add backup method"}
          </DialogHeader>

          <DialogBody className="gap-xl px-xl pt-xl sm:px-3xl sm:py-2xl">
            <SegmentedTabs<PaymentMethodType>
              className="w-full divide-x-0 p-px"
              onTabChange={handleTypeChange}
              selectedTab={methodType}
              tabClassName="flex-1 justify-center rounded-md py-[10px] text-md"
              tabs={[
                {
                  icon: <CreditCardIcon aria-hidden="true" size={20} />,
                  id: "card",
                  label: "Card",
                },
                {
                  icon: <LandmarkIcon aria-hidden="true" size={20} />,
                  id: "ach",
                  label: (
                    <>
                      <span className="sm:hidden">Bank (ACH)</span>
                      <span className="hidden sm:inline">
                        Bank account (ACH)
                      </span>
                    </>
                  ),
                },
              ]}
            />

            {methodType === "card" ? (
              <CardPaymentForm
                currentMethod={
                  currentMethod?.type === "card" ? currentMethod : null
                }
                formId={formId}
                onSubmit={submit.mutate}
                onValidityChange={setIsFormValid}
                setAsDefault={setAsDefault}
              />
            ) : (
              <AchPaymentForm
                currentMethod={
                  currentMethod?.type === "ach" ? currentMethod : null
                }
                formId={formId}
                onSubmit={submit.mutate}
                onValidityChange={setIsFormValid}
              />
            )}

            {slot?.kind === "default" ? (
              <span className="flex items-center gap-md text-sm font-medium text-text-tertiary">
                <CheckIcon aria-hidden="true" size={20} />
                This is your default payment method
              </span>
            ) : (
              <Checkbox isSelected={setAsDefault} onChange={setSetAsDefault}>
                Set as default payment method
              </Checkbox>
            )}

            {slot?.kind === "backup" && (
              <Button
                className="self-start p-0 hover:bg-transparent"
                color="gray"
                onPress={openRemoveConfirmation}
                size="sm"
                variant="tertiary"
              >
                <AccessibleIcon>
                  <Trash2Icon />
                </AccessibleIcon>
                Remove payment method
              </Button>
            )}

            {submit.error && (
              <p className="text-sm text-text-error-primary" role="alert">
                {submit.error.message}
              </p>
            )}
          </DialogBody>

          <DialogFooter className="px-xl pb-xl sm:border-t sm:border-border-secondary sm:px-3xl sm:pb-3xl">
            <Button
              className="sm:flex-1"
              color="gray"
              onPress={onClose}
              size="lg"
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              className="sm:flex-1"
              form={formId}
              isDisabled={!isFormValid || submit.isPending}
              isLoading={submit.isPending}
              size="lg"
              type="submit"
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </Dialog>
      </Modal>

      {currentMethod && (
        <ConfirmationModal
          confirmButtonText="Remove"
          icon={<Trash2Icon className="text-fg-error-primary" size={24} />}
          isConfirmLoading={removePaymentMethod.isPending}
          isOpen={isRemoveOpen}
          onConfirm={handleRemove}
          onOpenChange={setIsRemoveOpen}
          text={`${paymentMethodSummary(currentMethod)} will be permanently removed.${
            willReflowBackup
              ? " Backup method 2 will become backup method 1."
              : ""
          }`}
          title="Remove payment method?"
        >
          {removePaymentMethod.error && (
            <p className="text-sm text-text-error-primary" role="alert">
              {removePaymentMethod.error.message}
            </p>
          )}
        </ConfirmationModal>
      )}
    </>
  );
}
